import { StyleSheet, css } from 'aphrodite'
import { useState } from 'react'
import { useUser } from '../../contexts/UserContext'
import { userService } from '../../services/userService'
import { StakeholderType, UserType } from '../../types/user'
import Input from '../Input'
import Modal from '../Modal'
import Select from '../Select'
import SubmitButton from '../SubmitButton'
import { TextArea } from '../TextArea'

interface TeamInviteModalProps {
  onClose: () => void
  onSuccess: () => void
}

const USER_TYPE_OPTIONS = [
  { value: UserType.DRIVER as string, label: 'Driver' },
  { value: UserType.DISPATCHER as string, label: 'Dispatcher' },
  { value: UserType.PROJECT_MANAGER as string, label: 'Project Manager' },
  { value: UserType.SITE_SUPERVISOR as string, label: 'Site Supervisor' },
  { value: UserType.ADMINISTRATOR as string, label: 'Administrator' },
]

const STAKEHOLDER_TYPE_OPTIONS = [
  { value: StakeholderType.CONTRACTOR as string, label: 'Contractor' },
  { value: StakeholderType.BROKER as string, label: 'Broker' },
  { value: StakeholderType.SUB_HAULER as string, label: 'Sub Hauler' },
]

export const TeamInviteModal: React.FC<TeamInviteModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const { user } = useUser()
  const defaultStakeholderType =
    user?.current_profile?.stakeholder_type || StakeholderType.CONTRACTOR

  const [email, setEmail] = useState('')
  const [userType, setUserType] = useState<string>(UserType.ADMINISTRATOR)
  const [stakeholderType, setStakeholderType] = useState<string>(
    defaultStakeholderType,
  )
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      setError('Email is required')
      return
    }

    if (!userType) {
      setError('Please select a user type')
      return
    }

    if (note.length > 250) {
      setError('Note cannot exceed 250 characters')
      return
    }

    try {
      setLoading(true)
      setError(null)

      await userService.sendTeamInvite({
        email: email.trim(),
        user_type: userType as UserType,
        stakeholder_type: stakeholderType as StakeholderType,
        note: note.trim() || undefined,
      })

      onSuccess()
      onClose()
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to send team invitation',
      )
    } finally {
      setLoading(false)
    }
  }

  const selectedUserType = USER_TYPE_OPTIONS.find(
    (option) => option.value === userType,
  )

  const selectedStakeholderType = STAKEHOLDER_TYPE_OPTIONS.find(
    (option) => option.value === stakeholderType,
  )

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.header)}>
        <h2 className={css(styles.title)}>Invite to join as Team Member</h2>
        <button
          type="button"
          onClick={onClose}
          className={css(styles.closeButton)}
        >
          <i className="fa-solid fa-times" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className={css(styles.form)}>
        <div className={css(styles.field)}>
          <label className={css(styles.label)}>Email Address</label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@example.com"
            required
          />
        </div>

        <div className={css(styles.field)}>
          <label className={css(styles.label)}>Position</label>
          <p className={css(styles.hint)}>
            Select the team member's position within your organization
          </p>
          <Select
            value={selectedUserType}
            onChange={(option) =>
              setUserType(option?.value || UserType.ADMINISTRATOR)
            }
            options={USER_TYPE_OPTIONS}
            placeholder="Select position"
          />
        </div>

        <div className={css(styles.field)}>
          <label className={css(styles.label)}>Stakeholder Type</label>
          <p className={css(styles.hint)}>
            Select the stakeholder type for the team member
          </p>
          <Select
            value={selectedStakeholderType}
            onChange={(option) =>
              setStakeholderType(option?.value || StakeholderType.CONTRACTOR)
            }
            options={STAKEHOLDER_TYPE_OPTIONS}
            placeholder="Select stakeholder type"
          />
        </div>

        <div className={css(styles.field)}>
          <label className={css(styles.label)}>
            Note (optional)
            <span className={css(styles.charCount)}>{note.length}/250</span>
          </label>
          <TextArea
            value={note}
            onChange={(value: string) => setNote(value)}
            placeholder="Add a personal note to the invitation..."
            maxLength={250}
            rows={4}
          />
        </div>

        {error && <div className={css(styles.error)}>{error}</div>}

        <div className={css(styles.actions)}>
          <button
            type="button"
            onClick={onClose}
            className={css(styles.cancelButton)}
            disabled={loading}
          >
            Cancel
          </button>
          <SubmitButton canSubmit={!loading} text="Send Invitation" />
        </div>
      </form>
    </Modal>
  )
}

const styles = StyleSheet.create({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 20px 0 20px',
    borderBottom: '1px solid #E5E7EB',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    color: '#6B7280',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      color: '#1F2937',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '0 20px 20px 20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  charCount: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: 400,
  },
  hint: {
    fontSize: '12px',
    color: '#6B7280',
    margin: 0,
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#F9FAFB',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  checkboxText: {
    fontSize: '14px',
    color: '#1F2937',
  },
  error: {
    color: '#EF4444',
    fontSize: '14px',
    padding: '12px',
    backgroundColor: '#FEF2F2',
    borderRadius: '6px',
    border: '1px solid #FECACA',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '20px',
    borderTop: '1px solid #E5E7EB',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#6B7280',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#F9FAFB',
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  submitButton: {
    minWidth: '120px',
  },
})

export default TeamInviteModal
